import { useEffect, useState } from "react";
import authService from "../services/AuthService";

const MIN_DISPLAY_MS = 3200; // ensure at least one full message cycle is visible
const FADE_MS = 500;
// Hard ceiling on warmup work. MSAL's acquireTokenSilent (called by
// entraTokenRefresh) has NO internal timeout — a stale Entra session can make it
// never settle, which froze Promise.allSettled and hung the splash forever.
// Incident 2026-06-19 (admin-staging stuck on loading screen). Always let the
// app proceed to the normal auth flow / render after this cap.
const WARMUP_TIMEOUT_MS = 6000;

export type WarmupPhase = "loading" | "fading" | "ready";

export default function useWarmup() {
  const [phase, setPhase] = useState<WarmupPhase>("loading");

  useEffect(() => {
    const start = Date.now();

    async function warmup() {
      // Run warmup ping THEN startup token refresh (sequential — DB must be warm first).
      // The silent refresh attempts to renew an expired Entra token using the
      // cached MSAL account + active Entra session cookie, so users who return
      // after a computer restart are logged back in without a redirect to login.
      // Skip silent token refresh on the auth callback page — MSAL needs to process
      // the redirect response there first. Running acquireTokenSilent on the callback
      // URL triggers block_iframe_reload, which calls localOnlyLogout() and wipes the
      // PKCE verifier from sessionStorage before EntraCallback can consume it.
      const isCallbackPage = window.location.pathname.startsWith("/auth/");
      // Prime the DB connection pool BEFORE attempting token refresh.
      // The token exchange endpoint runs 3 SQL queries; on a cold post-deploy
      // server those hit cold connections (~10-15s) and time out. Running the
      // warmup ping first (which does SELECT 1) opens a connection so the auth
      // queries find it ready. Race both steps against the total cap.
      const warmupWork = (async () => {
        await fetch(import.meta.env.VITE_ADMIN_API_URL + "/warmup", { method: "post" }).catch(() => {});
        if (!authService.isAuthenticated() && authService.isEntraSession() && !isCallbackPage) {
          await authService.entraTokenRefresh().catch(() => {});
        }
      })();
      // Race against a hard timeout so a hung silent token renewal can never
      // freeze the splash. If the refresh is still pending after the cap, the app
      // proceeds and the normal auth flow (PrivateRoute) takes over.
      await Promise.race([
        warmupWork,
        new Promise((resolve) => setTimeout(resolve, WARMUP_TIMEOUT_MS)),
      ]);

      const elapsed = Date.now() - start;
      const remaining = Math.max(0, MIN_DISPLAY_MS - elapsed);

      // Ensure splash shows for at least MIN_DISPLAY_MS so it doesn't flash
      setTimeout(() => {
        setPhase("fading");
        // After fade-out completes, mark ready
        setTimeout(() => setPhase("ready"), FADE_MS);
      }, remaining);
    }
    warmup();
  }, []);

  return { phase };
}
